import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  DataTable,
  Dialog,
  EmptyState,
  ErrorState,
  Field,
  LoadingState,
  Select,
  TextArea,
  formatDate,
} from "@odingard/design-system";
import { ApiError } from "@odingard/sdk-client";
import type { IntakeStatus, VendorIntakeRequest } from "@odingard/sdk-client";
import { useAuth } from "../../lib/auth";
import { useAsync } from "../../lib/useAsync";
import { vendorApi } from "../../lib/api";
import { VendorDemoBanner } from "../../components/VendorMode";
import { TIER_LABEL } from "./labels";

const STATUSES: IntakeStatus[] = ["PENDING", "APPROVED", "REJECTED", "WITHDRAWN"];
const STATUS_TONE: Record<IntakeStatus, "neutral" | "success" | "warning" | "danger"> = {
  PENDING: "warning",
  APPROVED: "success",
  REJECTED: "danger",
  WITHDRAWN: "neutral",
};

export function VenIntake() {
  const { activeOrg } = useAuth();
  const orgId = activeOrg?.id ?? "";
  const [status, setStatus] = useState<IntakeStatus>("PENDING");
  const [deciding, setDeciding] = useState<VendorIntakeRequest | null>(null);

  const state = useAsync(async () => vendorApi.intakeRequests(orgId, { status }), [orgId, status]);
  const requests = state.data?.data.requests ?? [];
  const demoMode = state.data?.demoMode ?? false;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Intake queue</h1>
          <p>Requests to start using a vendor. Someone other than the requester has to decide each one.</p>
        </div>
        <div style={{ minWidth: "12rem" }}>
          <Field label="Status">
            {(id) => (
              <Select id={id} value={status} onChange={(e) => setStatus(e.target.value as IntakeStatus)}>
                {STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {s.charAt(0) + s.slice(1).toLowerCase()}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        </div>
      </div>

      <VendorDemoBanner show={demoMode} />

      {state.loading && <LoadingState label="Loading intake requests…" />}
      {state.error && <ErrorState description={state.error.message} onRetry={state.reload} />}
      {state.data && requests.length === 0 && (
        <EmptyState title="Nothing here" description="No intake request is in this state." />
      )}
      {requests.length > 0 && (
        <Card>
          <CardHeader title="Requests" />
          <CardBody>
            <DataTable
              columns={[
                {
                  key: "vendorId",
                  header: "Vendor",
                  render: (r: VendorIntakeRequest) => (
                    <Link to={`/studio/vendor-ai/vendors/${r.vendorId}`}>{r.vendorId}</Link>
                  ),
                },
                { key: "intendedUse", header: "Intended use", render: (r: VendorIntakeRequest) => r.intendedUse },
                {
                  key: "requestedTier",
                  header: "Requested tier",
                  render: (r: VendorIntakeRequest) =>
                    r.requestedTier ? TIER_LABEL[r.requestedTier] : "Not stated",
                },
                {
                  key: "status",
                  header: "Status",
                  render: (r: VendorIntakeRequest) => <Badge tone={STATUS_TONE[r.status]}>{r.status}</Badge>,
                },
                { key: "createdAt", header: "Raised", render: (r: VendorIntakeRequest) => formatDate(r.createdAt) },
                {
                  key: "decide",
                  header: "",
                  render: (r: VendorIntakeRequest) =>
                    r.status === "PENDING" ? (
                      <Button size="sm" onClick={() => setDeciding(r)}>
                        Decide
                      </Button>
                    ) : (
                      <span style={{ color: "var(--ods-color-muted)" }}>{r.decisionReason ?? "—"}</span>
                    ),
                },
              ]}
              rows={requests}
              caption="Vendor intake requests"
            />
          </CardBody>
        </Card>
      )}

      <DecisionDialog
        request={deciding}
        orgId={orgId}
        onClose={() => setDeciding(null)}
        onDecided={() => {
          setDeciding(null);
          state.reload();
        }}
      />
    </>
  );
}

/**
 * Three refusals a decider can meet, and they mean different things: the edge
 * says this user does not hold vendors.approve, the backend says this user
 * cannot decide their own request, and a version conflict says the request
 * moved after this screen read it. Collapsing them into "failed" would leave
 * the decider guessing which one to act on.
 */
function refusalMessage(err: unknown): string {
  if (!(err instanceof ApiError)) return "Could not record this decision.";
  if (err.code === "forbidden" && typeof err.details?.permission === "string") {
    return `You do not hold ${err.details.permission}, so you cannot decide vendor intake. Ask an owner or admin to grant it or to decide this request.`;
  }
  if (err.code === "segregation_of_duties") {
    return "You raised this request, so somebody else has to decide it. That separation is enforced by Vendor AI, not by this screen.";
  }
  if (err.code === "conflict" || err.status === 409) {
    return `${err.message} Reload the queue before deciding again.`;
  }
  return err.message;
}

function DecisionDialog({
  request,
  orgId,
  onClose,
  onDecided,
}: {
  request: VendorIntakeRequest | null;
  orgId: string;
  onClose: () => void;
  onDecided: () => void;
}) {
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function decide(decision: "APPROVED" | "REJECTED") {
    if (!request) return;
    setSaving(true);
    setError(null);
    try {
      await vendorApi.decideIntake(orgId, request.id, {
        decision,
        reason: reason.trim(),
        expectedVersion: request.version,
      });
      setReason("");
      onDecided();
    } catch (err) {
      setError(refusalMessage(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog
      open={request !== null}
      title="Decide this intake request"
      description={request?.businessJustification}
      onClose={onClose}
      dismissible={!saving}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button variant="danger" onClick={() => decide("REJECTED")} disabled={saving || reason.trim() === ""}>
            Reject
          </Button>
          <Button onClick={() => decide("APPROVED")} disabled={saving || reason.trim() === ""}>
            Approve
          </Button>
        </>
      }
    >
      {error && <ErrorState title="Not recorded" description={error} />}
      <Field label="Reason" help="Recorded against the decision and kept in the request's decision history.">
        {(id) => <TextArea id={id} rows={4} value={reason} onChange={(e) => setReason(e.target.value)} />}
      </Field>
    </Dialog>
  );
}
